// ============================================================================
// BP-13-02: Admin Analytics Hooks
// BrightPath Platform - Admin Frontend
// ============================================================================

import { useQuery, useMutation } from '@tanstack/react-query';
import { adminAnalyticsApi, adminSystemApi } from '@/features/admin/api/adminApi';
import type { AnalyticsRequest } from '@/types/admin';
import { useToast } from '@/core/hooks/useToast';

// =============================================================================
// Query Keys
// =============================================================================

export const analyticsKeys = {
  all: ['admin', 'analytics'] as const,
  dashboard: () => [...analyticsKeys.all, 'dashboard'] as const,
  users: (params: AnalyticsRequest) => [...analyticsKeys.all, 'users', params] as const,
  engagement: (params: AnalyticsRequest) =>
    [...analyticsKeys.all, 'engagement', params] as const,
  content: (params: AnalyticsRequest) => [...analyticsKeys.all, 'content', params] as const,
  marketplace: (params: AnalyticsRequest) =>
    [...analyticsKeys.all, 'marketplace', params] as const,
  system: () => [...analyticsKeys.all, 'system'] as const,
};

// =============================================================================
// Dashboard
// =============================================================================

/**
 * Fetch admin dashboard summary
 */
export function useAdminDashboard() {
  return useQuery({
    queryKey: analyticsKeys.dashboard(),
    queryFn: () => adminAnalyticsApi.getDashboard(),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
}

// =============================================================================
// User Analytics
// =============================================================================

/**
 * Fetch user analytics
 */
export function useUserAnalytics(params: AnalyticsRequest) {
  return useQuery({
    queryKey: analyticsKeys.users(params),
    queryFn: () => adminAnalyticsApi.getUserAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Engagement Analytics
// =============================================================================

/**
 * Fetch engagement analytics
 */
export function useEngagementAnalytics(params: AnalyticsRequest) {
  return useQuery({
    queryKey: analyticsKeys.engagement(params),
    queryFn: () => adminAnalyticsApi.getEngagementAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Content Analytics
// =============================================================================

/**
 * Fetch content analytics
 */
export function useContentAnalytics(params: AnalyticsRequest) {
  return useQuery({
    queryKey: analyticsKeys.content(params),
    queryFn: () => adminAnalyticsApi.getContentAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// Marketplace Analytics
// =============================================================================

/**
 * Fetch marketplace analytics
 */
export function useMarketplaceAnalytics(params: AnalyticsRequest) {
  return useQuery({
    queryKey: analyticsKeys.marketplace(params),
    queryFn: () => adminAnalyticsApi.getMarketplaceAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// =============================================================================
// System Health
// =============================================================================

/**
 * Fetch system health status
 */
export function useSystemHealth() {
  return useQuery({
    queryKey: analyticsKeys.system(),
    queryFn: () => adminSystemApi.getHealth(),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  });
}

// =============================================================================
// Export
// =============================================================================

/**
 * Export analytics report
 */
export function useExportAnalytics() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      reportType,
      params,
    }: {
      reportType: 'users' | 'engagement' | 'content' | 'marketplace';
      params: AnalyticsRequest;
    }) => adminAnalyticsApi.exportReport(reportType, params),
    onSuccess: (blob, { reportType }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Export complete',
        description: `${reportType} analytics report has been downloaded.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Export failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

// =============================================================================
// Convenience Hook for All Analytics
// =============================================================================

/**
 * Fetch all analytics data for a given period
 */
export function useAllAnalytics(params: AnalyticsRequest) {
  const userAnalytics = useUserAnalytics(params);
  const engagementAnalytics = useEngagementAnalytics(params);
  const contentAnalytics = useContentAnalytics(params);
  const marketplaceAnalytics = useMarketplaceAnalytics(params);

  const isLoading =
    userAnalytics.isLoading ||
    engagementAnalytics.isLoading ||
    contentAnalytics.isLoading ||
    marketplaceAnalytics.isLoading;

  const isError =
    userAnalytics.isError ||
    engagementAnalytics.isError ||
    contentAnalytics.isError ||
    marketplaceAnalytics.isError;

  return {
    userAnalytics: userAnalytics.data,
    engagementAnalytics: engagementAnalytics.data,
    contentAnalytics: contentAnalytics.data,
    marketplaceAnalytics: marketplaceAnalytics.data,
    isLoading,
    isError,
    refetch: () => {
      userAnalytics.refetch();
      engagementAnalytics.refetch();
      contentAnalytics.refetch();
      marketplaceAnalytics.refetch();
    },
  };
}


